/**
 * D3.jsグラフ共通ヘルパー関数群 (src/js/utils/d3-helpers.ts)
 */
import { Selection, BaseType } from 'd3';

// D3セレクションの型エイリアスを定義
type D3Selection<T extends BaseType> = Selection<T, unknown, null, undefined>;

/**
 * コンテナの幅に基づいて、要素に特定のCSSクラスを付け外しする。
 * JS（振る舞い）とCSS（見た目）の責務分離を促進するレスポンシブ対応ヘルパー。
 *
 * @param {D3Selection<T> | null} selection - クラスを操作する対象のD3セレクション。
 * @param {number} containerWidth - 親コンテナの現在の幅。
 * @param {number} breakpoint - クラスを切り替える幅の閾値。
 * @param {string} className - 付け外しするクラス名。
 */
function toggleClassByWidth<T extends BaseType>(
  selection: D3Selection<T> | null,
  containerWidth: number,
  breakpoint: number,
  className: string
): void {
  // 対象のセレクションが存在しない、または空の場合は何もしない
  if (!selection || selection.empty()) {
    return;
  }
  selection.classed(className, containerWidth < breakpoint);
}

/**
 * 日本語環境向けの万進法（万・億）数値フォーマッタ。
 * D3標準の `d3.format("~s")` の日本語代替として、軸のラベル等で使用する。
 * 
 * @param {number | { valueOf(): number }} d - フォーマットする数値
 * @returns {string} フォーマットされた文字列 (例: "1.5万", "2億")
 */
function formatJapaneseUnit(d: number | { valueOf(): number }): string {
    // d3のtickFormatから渡されるオブジェクトを数値に変換
    const value = typeof d === 'number' ? d : d.valueOf();
    
    if (value === 0) return "0";
    
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";

    if (absValue >= 100000000) {
        // 1億以上: 小数点第1位まで表示 (ただし .0 は省略する)
        const formatted = (absValue / 100000000).toLocaleString('ja-JP', { maximumFractionDigits: 1 });
        return `${sign}${formatted}億`;
    } else if (absValue >= 10000) {
        // 1万以上: 小数点第1位まで表示 (ただし .0 は省略する)
        const formatted = (absValue / 10000).toLocaleString('ja-JP', { maximumFractionDigits: 1 });
        return `${sign}${formatted}万`;
    } else {
        // 1万未満: カンマ区切り
        return `${sign}${absValue.toLocaleString('ja-JP')}`;
    }
}

// ============================================================================

// ESモジュールとしてエクスポートする
export { toggleClassByWidth, formatJapaneseUnit }; // 【修正】formatJapaneseUnit をエクスポートに追加